import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth';
import {
    LayoutDashboard,
    Newspaper,
    KanbanSquare,
    Users,
    UserCheck,
    GraduationCap,
    LogOut,
    Settings,
    Building2,
    User,
    ChevronDown,
    ListTodo,
    Calendar,
    LifeBuoy,
    Search,
    X,
    Sparkles,
    Wrench,
    UserPlus,
    Lightbulb,
    FileText,
    ExternalLink,
    Gauge,
    FolderKanban,
    CheckSquare,
    ChevronRight,
    Network,
    Rocket,
    Menu
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { hubAPI } from '@/lib/api';
import { GlobalSearch } from '@/components/common/GlobalSearch';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useUsefulDocsUnread } from '@/hooks/useUsefulDocsUnread';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ROLE_NAMES = {
    admin_full: 'Admin Full',
    admin: 'Admin',
    comptable: 'Comptable',
    vp: 'VP',
    apporteur: 'Apporteur'
};

const TOP_ITEMS = [
    { icon: Rocket, label: 'Projets', path: '/projets', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'], requiresHubAccess: true, isNew: true },
    { icon: Newspaper, label: "Fil d'actualité", path: '/feed', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: Sparkles, label: 'Inside VNX', path: '/inside-vnx', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable'] },
];

const NAV_GROUPS = [
    {
        id: 'dossiers', label: 'Dossiers', icon: FolderKanban,
        items: [
            { icon: Users, label: 'Prospects', path: '/prospects', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'parrain'] },
            { icon: UserCheck, label: 'Clients', path: '/clients', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
            { icon: KanbanSquare, label: 'Pipeline', path: '/pipeline', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
        ],
    },
    {
        id: 'equipe', label: "Gestion d'équipe", icon: Building2,
        items: [
            { icon: UserPlus, label: 'Onboarding', path: '/onboarding', roles: ['admin_full', 'admin', 'vp'] },
            { icon: Network, label: 'Réseau Parrainage', path: '/parrainage', roles: ['admin_full', 'admin', 'vp', 'parrain'], requiresParrainage: true, isNew: true },
            { icon: Gauge, label: 'Implication', path: '/implication', roles: ['admin_full', 'admin', 'vp'], isNew: true },
            { icon: Building2, label: 'Équipes', path: '/teams', roles: ['admin_full', 'admin', 'vp'] },
            { icon: FileText, label: 'Candidatures', href: 'https://valnexo.fr/admin/candidatures', external: true, roles: ['admin_full', 'admin', 'vp'], isNew: true },
        ],
    },
    {
        id: 'actions', label: 'Actions', icon: CheckSquare,
        items: [
            { icon: ListTodo, label: 'Tâches', path: '/tasks', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
            { icon: Calendar, label: 'Calendrier', path: '/calendar', roles: ['admin_full', 'admin', 'vp', 'apporteur'] },
        ],
    },
    {
        id: 'supports', label: 'Supports', icon: LifeBuoy,
        items: [
            { icon: GraduationCap, label: 'Académie', path: '/academy', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'apprenant', 'parrain'] },
            { icon: Lightbulb, label: 'Boîte à idées', path: '/boite-a-idees', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'], isNew: true },
            { icon: Wrench, label: 'Outils & liens utiles', path: '/outils', roles: ['admin_full', 'admin', 'comptable', 'vp', 'apporteur', 'parrain'] },
            { icon: LifeBuoy, label: 'Support', path: '/support', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'apprenant', 'parrain'] },
        ],
    },
];

const BOTTOM_ITEMS = [
    { icon: Settings, label: 'Paramètres', path: '/settings', roles: ['admin_full', 'admin', 'vp', 'apporteur', 'comptable', 'parrain'] },
];

// Liste à plat (rétro-compat export)
const allMenuItems = [...TOP_ITEMS, ...NAV_GROUPS.flatMap(g => g.items), ...BOTTOM_ITEMS];

// Pastille « Nouveau » animée (partagée desktop/mobile via localStorage)
const SEEN_NAV_KEY = 'vnx_seen_nav_v1';
const NewBadge = () => (
    <span
        className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-2 py-0.5 text-[10px] font-semibold text-yellow-400 ring-1 ring-yellow-500/40"
        data-testid="nav-new-badge-mobile"
    >
        <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-yellow-400" />
        </span>
        Nouveau
    </span>
);

// (Ancienne barre du bas supprimée — remplacée par le bouton menu flottant auto-masquable.)

// Composant Avatar utilisateur avec fallback
const UserAvatar = ({ user, size = 'md' }) => {
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base'
    };

    const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
    
    // Supporte les deux formats : avatar_url et profile_picture
    const avatarUrl = user?.avatar_url || user?.profile_picture;

    if (avatarUrl) {
        return (
            <img 
                src={avatarUrl} 
                alt={`${user?.first_name} ${user?.last_name}`}
                className={cn(
                    "rounded-full object-cover ring-2 ring-white/20",
                    sizeClasses[size]
                )}
            />
        );
    }

    if (initials) {
        return (
            <div className={cn(
                "rounded-full bg-gradient-to-br from-yellow-600 to-yellow-500 flex items-center justify-center font-semibold text-white ring-2 ring-white/20",
                sizeClasses[size]
            )}>
                {initials}
            </div>
        );
    }

    return (
        <div className={cn(
            "rounded-full bg-slate-600 flex items-center justify-center ring-2 ring-white/20",
            sizeClasses[size]
        )}>
            <User className="w-1/2 h-1/2 text-slate-300" />
        </div>
    );
};

// Composant UserMenuTrigger - Déclencheur du menu utilisateur
const UserMenuTrigger = ({ user, onClick, className }) => {
    return (
        <button
            onClick={onClick}
            className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all",
                "hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-500/50",
                "cursor-pointer select-none",
                className
            )}
            aria-label="Ouvrir le menu utilisateur"
            data-testid="user-menu-trigger"
        >
            <UserAvatar user={user} size="sm" />
            
            {/* Desktop: Prénom NOM */}
            <span className="hidden lg:block text-sm font-medium text-white max-w-[150px] truncate">
                {user?.first_name} {user?.last_name?.toUpperCase()}
            </span>
            
            {/* Tablet: Prénom seul */}
            <span className="hidden sm:block lg:hidden text-sm font-medium text-white">
                {user?.first_name}
            </span>
            
            {/* Mobile (<480px): Avatar seul - pas de texte */}
            
            <ChevronDown className="hidden sm:block w-4 h-4 text-slate-400" />
        </button>
    );
};

export const MobileNav = () => {
    const location = useLocation();
    const { user, logout, isTrainingRestricted, unreadFeedCount, wallUnreadCount } = useAuth();
    const usefulDocsUnread = useUsefulDocsUnread(user?.id);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    // Bouton menu flottant : visible par défaut, se masque au scroll vers le bas,
    // réapparaît au scroll vers le haut ou à l'arrêt (évite le bug de barre fixe iOS).
    const [fabVisible, setFabVisible] = useState(true);
    useEffect(() => {
        let lastY = window.scrollY || 0;
        let timer = null;
        const onScroll = (e) => {
            const el = e.target;
            const y = (!el || el === document || el === document.documentElement || el === document.body)
                ? (window.scrollY || 0)
                : (typeof el.scrollTop === 'number' ? el.scrollTop : (window.scrollY || 0));
            if (y > lastY + 6 && y > 64) setFabVisible(false);
            else if (y < lastY - 6) setFabVisible(true);
            lastY = y;
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setFabVisible(true), 650);
        };
        document.addEventListener('scroll', onScroll, true);
        return () => { document.removeEventListener('scroll', onScroll, true); if (timer) clearTimeout(timer); };
    }, []);
    const [seenNav, setSeenNav] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SEEN_NAV_KEY) || '[]'); } catch { return []; }
    });

    // Compteur global de non-lus sur les projets Hub (badge menu Projets mobile)
    const [hubUnread, setHubUnread] = useState(0);
    useEffect(() => {
        let alive = true;
        const fetchHub = async () => {
            try {
                const r = await hubAPI.unreadCount();
                if (alive) setHubUnread(r.data?.count || 0);
            } catch { /* noop */ }
        };
        fetchHub();
        const iv = setInterval(fetchHub, 45000);
        const onSeen = () => fetchHub();
        window.addEventListener('hub:activity-seen', onSeen);
        return () => { alive = false; clearInterval(iv); window.removeEventListener('hub:activity-seen', onSeen); };
    }, []);

    const markNavSeen = (id) => {
        setSeenNav((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            try { localStorage.setItem(SEEN_NAV_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };

    const handleLogout = () => {
        setSheetOpen(false);
        setDropdownOpen(false);
        logout();
    };

    const restricted = isTrainingRestricted();
    // Aligné avec _is_admin() du backend : accepte le flag OU le role admin_full/admin
    const userHasAdminAccess = user?.is_admin === true || user?.role === 'admin_full' || user?.role === 'admin';
    const hubAccessLevel = user?.hub_access || 'none';
    const userHasHubAccess = userHasAdminAccess || hubAccessLevel === 'full' || hubAccessLevel === 'restricted';
    const isItemVisible = (item) => {
        const roleAllowed = item.roles.includes(user?.role)
            || (userHasAdminAccess && (item.roles.includes('admin_full') || item.roles.includes('admin')));
        if (!roleAllowed) return false;
        if (item.requiresParrainage) {
            // Réseau Parrainage activé par défaut pour TOUS les VP (plus de switch).
        }
        if (item.requiresHubAccess && !userHasHubAccess) return false;
        if (restricted && !['/academy', '/support'].includes(item.path)) return false;
        return true;
    };
    const topItems = TOP_ITEMS.filter(isItemVisible);
    const bottomItems = BOTTOM_ITEMS.filter(isItemVisible);
    const visibleGroups = NAV_GROUPS
        .map(g => ({ ...g, items: g.items.filter(isItemVisible) }))
        .filter(g => g.items.length > 0);

    const [openGroups, setOpenGroups] = useState({});
    useEffect(() => {
        const activeGroup = NAV_GROUPS.find(g => g.items.some(it => it.path && location.pathname.startsWith(it.path)));
        if (activeGroup) setOpenGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }, [location.pathname]);
    const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

    const renderMobileItem = (item, { nested = false } = {}) => {
        const isActive = item.path && location.pathname === item.path;
        const showFeedBadge = item.path === '/feed' && unreadFeedCount > 0;
        const showWallBadge = item.path === '/prospects' && wallUnreadCount > 0;
        const showHubBadge = item.path === '/projets' && hubUnread > 0;
        const showDocsBadge = item.path === '/outils' && usefulDocsUnread > 0;
        const badgeCount = showFeedBadge ? unreadFeedCount : (showWallBadge ? wallUnreadCount : (showDocsBadge ? usefulDocsUnread : (showHubBadge ? hubUnread : 0)));
        const navId = item.path || item.href;
        const showNew = item.isNew && !seenNav.includes(navId);
        const padClass = nested ? 'pl-10 pr-4 py-2.5' : 'px-4 py-3';
        if (item.external) {
            return (
                <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { markNavSeen(navId); setSheetOpen(false); }}
                    data-testid="mobile-candidatures-link"
                    className={cn("flex items-center gap-3 rounded-xl transition-all text-slate-300 hover:bg-white/10 hover:text-white", padClass)}
                >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium flex-1">{item.label}</span>
                    {showNew ? <NewBadge /> : <ExternalLink className="w-4 h-4 text-slate-500" />}
                </a>
            );
        }
        return (
            <Link
                key={item.path}
                to={item.path}
                reloadDocument={item.path === '/inside-vnx'}
                onClick={() => { if (showNew) markNavSeen(navId); setSheetOpen(false); }}
                data-testid={`mobile-menu-link-${(item.path || '').replace('/', '')}`}
                className={cn(
                    "flex items-center gap-3 rounded-xl transition-all",
                    padClass,
                    isActive ? "bg-yellow-600 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                )}
            >
                <item.icon className="w-5 h-5" />
                <span className="font-medium flex-1">{item.label}</span>
                {(showFeedBadge || showWallBadge || showHubBadge || showDocsBadge) && (
                    <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold leading-none"
                        data-testid={`${showWallBadge ? 'wall' : (showDocsBadge ? 'useful-docs' : (showHubBadge ? 'hub' : 'feed'))}-unread-badge-mobile`}>
                        {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                )}
                {showNew && !(showFeedBadge || showWallBadge || showHubBadge || showDocsBadge) && <NewBadge />}
            </Link>
        );
    };

    // Fermer le dropdown avec Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    return (
        <>
            {/* Top bar - visible sur mobile et tablet, avec menu utilisateur */}
            <header 
                className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 border-b border-white/10 flex items-center justify-between px-4 z-50"
                style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(3.5rem + env(safe-area-inset-top, 0px))', transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', willChange: 'transform' }}
            >
                {/* Logo */}
                <Link to="/dashboard" className="flex items-center gap-2">
                    <img 
                        src="/valnexo-logo-official-new.png"
                        alt="Valnexo Logo"
                        className="w-7 h-7 object-contain rounded"
                    />
                    <span className="font-bold text-base text-white">Valnexo</span>
                </Link>
                
                {/* User Menu Trigger - remplace le hamburger */}
                <div className="flex items-center gap-1">
                    {/* Bouton recherche -> ouvre l'overlay plein écran */}
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                        aria-label="Rechercher"
                        data-testid="mobile-search-trigger"
                    >
                        <Search className="w-5 h-5 text-white" />
                    </button>
                    {/* Cloche notifications (entre la loupe et la photo de profil) */}
                    <div data-testid="mobile-notification-bell">
                        <NotificationCenter bellTestId="mobile-header-notification-bell" />
                    </div>
                    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                        <div>
                            <UserMenuTrigger user={user} onClick={() => setSheetOpen(true)} />
                        </div>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-80 max-w-[100vw] p-0 bg-slate-900 border-l border-white/10" data-testid="mobile-navigation-dialog">
                        <SheetTitle className="sr-only" data-testid="mobile-navigation-title">Navigation principale</SheetTitle>
                        <SheetDescription className="sr-only" data-testid="mobile-navigation-description">Rubriques du CRM et profil utilisateur.</SheetDescription>
                        <div className="flex flex-col h-full">
                            {/* Header avec infos utilisateur */}
                            <div className="p-4 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <UserAvatar user={user} size="lg" />
                                    <div className="flex-1">
                                        <p className="text-white font-medium">
                                            {user?.first_name} {user?.last_name?.toUpperCase()}
                                        </p>
                                        <Badge className="mt-1 bg-yellow-600/20 text-yellow-400 text-xs border-0">
                                            {ROLE_NAMES[user?.role] || user?.role}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Menu Items */}
                            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                                {topItems.map((item) => renderMobileItem(item))}

                                {visibleGroups.map((group) => {
                                    const isOpen = !!openGroups[group.id];
                                    const hasActive = group.items.some(it => it.path && location.pathname === it.path);
                                    return (
                                        <div key={group.id} className="pt-2">
                                            <button
                                                type="button"
                                                onClick={() => toggleGroup(group.id)}
                                                data-testid={`mobile-group-${group.id}`}
                                                aria-expanded={isOpen}
                                                className={cn(
                                                    "w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all hover:bg-white/5",
                                                    hasActive ? "text-white" : "text-slate-400"
                                                )}
                                            >
                                                <group.icon className={cn("w-5 h-5", hasActive ? "text-yellow-500" : "text-slate-500")} />
                                                <span className="font-semibold text-xs uppercase tracking-wide flex-1 text-left">{group.label}</span>
                                                <ChevronRight className={cn("w-4 h-4 text-slate-500 transition-transform", isOpen && "rotate-90")} />
                                            </button>
                                            <div className={cn(
                                                "overflow-hidden transition-all duration-200 space-y-1",
                                                isOpen ? "max-h-[600px] mt-1 opacity-100" : "max-h-0 opacity-0"
                                            )}>
                                                {group.items.map((item) => renderMobileItem(item, { nested: true }))}
                                            </div>
                                        </div>
                                    );
                                })}

                                {bottomItems.length > 0 && (
                                    <div className="pt-3 mt-2 border-t border-white/10">
                                        {bottomItems.map((item) => renderMobileItem(item))}
                                    </div>
                                )}
                            </nav>

                            {/* Logout Button */}
                            <div className="p-4 border-t border-white/10">
                                <Button
                                    onClick={handleLogout}
                                    variant="destructive"
                                    className="w-full bg-red-600 hover:bg-red-700"
                                    data-testid="mobile-logout-btn"
                                >
                                    <LogOut className="w-5 h-5 mr-2" />
                                    Déconnexion
                                </Button>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
                </div>
            </header>

            {/* Overlay de recherche plein écran (mobile) */}
            {searchOpen && (
                <div
                    className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col"
                    data-testid="mobile-search-overlay"
                >
                    <div
                        className="flex items-center gap-2 px-4 border-b border-slate-200 bg-white"
                        style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top, 0px))', paddingBottom: '0.5rem' }}
                    >
                        <div className="flex-1">
                            <GlobalSearch
                                variant="mobile"
                                showCallButton
                                autoFocus
                                onResultNavigate={() => setSearchOpen(false)}
                            />
                        </div>
                        <button
                            onClick={() => setSearchOpen(false)}
                            className="p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
                            aria-label="Fermer la recherche"
                            data-testid="mobile-search-close"
                        >
                            <X className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>
                </div>
            )}

            {/* Bouton menu flottant (mobile) — remplace l'ancienne barre du bas fixe (bug iOS).
                Rendu via PORTAIL sur document.body. S'auto-masque au scroll vers le bas et
                réapparaît au scroll vers le haut / à l'arrêt. Ouvre le menu complet groupé. */}
            {createPortal(
                <button
                    type="button"
                    onClick={() => { try { navigator.vibrate && navigator.vibrate(12); } catch (_) {} setSheetOpen(true); }}
                    aria-label="Ouvrir le menu"
                    data-testid="mobile-menu-fab"
                    className="md:hidden fixed z-50 flex items-center justify-center rounded-full bg-slate-900 text-white shadow-xl shadow-black/30 active:scale-90"
                    style={{
                        right: '1rem',
                        bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
                        width: '3.5rem',
                        height: '3.5rem',
                        opacity: (fabVisible || sheetOpen) ? 1 : 0,
                        transform: (fabVisible || sheetOpen) ? 'translateY(0) scale(1)' : 'translateY(140%) scale(0.7)',
                        pointerEvents: (fabVisible || sheetOpen) ? 'auto' : 'none',
                        transition: (fabVisible || sheetOpen)
                            ? 'opacity 0.18s ease-out, transform 0.42s cubic-bezier(0.34, 1.56, 0.64, 1)'
                            : 'opacity 0.22s ease-in, transform 0.28s cubic-bezier(0.4, 0, 1, 1)',
                    }}
                >
                    <Menu className="w-6 h-6" />
                    {(unreadFeedCount > 0 || wallUnreadCount > 0) && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-slate-900"
                            data-testid="mobile-fab-badge">
                            {(unreadFeedCount + wallUnreadCount) > 9 ? '9+' : (unreadFeedCount + wallUnreadCount)}
                        </span>
                    )}
                </button>,
                document.body
            )}
        </>
    );
};

// Export UserAvatar et UserMenuTrigger pour réutilisation
export { UserAvatar, UserMenuTrigger, ROLE_NAMES, allMenuItems };
